import React, { useState, useEffect, useRef } from 'react';

import { Modal } from 'flowbite';

import ExpenseLibraryTemplatesEditModal from './ExpenseLibraryTemplatesEditModal';
import ExpenseLibraryTemplatesIndexTableRow from './ExpenseLibraryTemplatesIndexTableRow';

import { modalOptions } from 'Helpers/modalOptions';

import Button from 'CommonComponents/Button';
import ConfirmationModal from 'CommonComponents/ConfirmationModal';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import TablePagination from 'CommonComponents/TablePagination';
import TableSearchInput from 'CommonComponents/TableSearchInput';

import PlusIcon from 'Assets/icons/plus.svg';

export default function ExpenseLibraryTemplatesIndexTable(props) {
    const [templates, setTemplates] = useState([]);
    const [upsertedTemplate, setUpsertedTemplate] = useState(undefined);
    const [deletedTemplate, setDeletedTemplate] = useState(undefined);
    const [perPage] = useState(10);
    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [to, setTo] = useState(1);
    const [from, setFrom] = useState(1);
    const [total, setTotal] = useState(1);
    const [isLoading, setIsLoading] = useState(1);
    const [searchValue, setSearchValue] = useState('');

    const [actionCompleted, setActionCompleted] = useState(false);
    const [actionInProgress, setActionInProgress] = useState(false);
    const [actionFailed, setActionFailed] = useState(false);

    const [expenseLibraryTemplatesEditModalElement, setExpenseLibraryTemplatesEditModalElement] = useState(null);
    const [expenseLibraryTemplatesDeleteModalElement, setExpenseLibraryTemplatesDeleteModalElement] = useState(null);

    const expenseLibraryTemplatesEditModalRef = useRef();
    const expenseLibraryTemplatesDeleteModalRef = useRef();

    useEffect(() => {
        fetchTemplates();
        expenseLibraryTemplatesEditModalRef &&
            setExpenseLibraryTemplatesEditModalElement(new Modal(expenseLibraryTemplatesEditModalRef.current, modalOptions(false)));
        expenseLibraryTemplatesDeleteModalRef &&
            setExpenseLibraryTemplatesDeleteModalElement(new Modal(expenseLibraryTemplatesDeleteModalRef.current, modalOptions(false)));
    }, []);

    useEffect(() => {
        if (upsertedTemplate !== undefined) {
            expenseLibraryTemplatesEditModalElement && expenseLibraryTemplatesEditModalElement.show();
        }
    }, [upsertedTemplate]);

    useEffect(() => {
        if (deletedTemplate !== undefined) {
            expenseLibraryTemplatesDeleteModalElement && expenseLibraryTemplatesDeleteModalElement.show();
        }
    }, [deletedTemplate]);

    const defaultUrl = props.templatesIndexUrl + '?per_page=' + perPage;

    const combineFetchUrl = (p = null, pp = null) => {
        return props.templatesIndexUrl + '?per_page=' + (pp || perPage) + '&page=' + (p || page) + (searchValue.length > 0 ? '&search=' + searchValue : '');
    };

    const fetchTemplates = (url = null) => {
        setIsLoading(true);
        const fetchUrl = url || defaultUrl;

        axios
            .get(fetchUrl)
            .then((res) => {
                const { data, to, from, total, last_page } = res.data;
                setTemplates(data);
                setTo(to);
                setFrom(from);
                setTotal(total);
                setLastPage(last_page);
                setIsLoading(false);
            })
            .catch((error) => {
                setTemplates([]);
                setIsLoading(false);
            });
    };

    function pageChange(data) {
        const url = combineFetchUrl(data.selected + 1);
        setPage(+data.selected + 1);
        fetchTemplates(url);
    }

    const handleChange = (event) => {
        setSearchValue(event.target.value);
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            const url = combineFetchUrl(1);
            setPage(1);
            fetchTemplates(url);
        }
    };

    const closeModal = () => {
        setUpsertedTemplate(undefined);
        setDeletedTemplate(undefined);
        expenseLibraryTemplatesEditModalElement && expenseLibraryTemplatesEditModalElement.hide();
        expenseLibraryTemplatesDeleteModalElement && expenseLibraryTemplatesDeleteModalElement.hide();
    };

    const successHandler = () => {
        setUpsertedTemplate(undefined);
        setDeletedTemplate(undefined);
        setTemplates([]);
        const url = combineFetchUrl(page);
        fetchTemplates(url);
        expenseLibraryTemplatesEditModalElement && expenseLibraryTemplatesEditModalElement.hide();
        expenseLibraryTemplatesDeleteModalElement && expenseLibraryTemplatesDeleteModalElement.hide();
    };

    function clickRow(template) {
        setUpsertedTemplate(template);
    }

    function deleteRow(template) {
        setDeletedTemplate(template);
    }

    const cleanConfirmationModalStates = () => {
        setActionInProgress(false);
        setActionFailed(false);
        setActionCompleted(false);
    };

    const triggerDeleteTemplate = () => {
        setActionInProgress(true);

        axios
            .delete(props.templatesDeleteUrl.replace('EXPENSE_LIBRARY_TEMPLATE', deletedTemplate.id), { data: {} })
            .then((response) => {
                setActionCompleted(true);
            })
            .catch((e) => {
                setActionFailed(true);
            });
    };

    return (
        <div className="table__card">
            <div className="tw-flex tw-items-start tw-justify-between tw-mb-6">
                <div>
                    <h5 className="table__header">{$t('Expense Template Library')}</h5>
                    <div className="tw-flex tw-items-center tw-space-x-3">
                        <PaginationInfo to={to} from={from} total={total} />
                        <TablePagination lastPage={lastPage} pageChange={pageChange} />
                    </div>
                </div>
                <div className="tw-flex tw-gap-x-1.5">
                    <TableSearchInput searchValue={searchValue} handleChange={handleChange} handleKeyPress={handleKeyPress} />
                    <Button variant="primary" onClick={() => setUpsertedTemplate({})}>
                        <PlusIcon className="tw-w-3 tw-h-3" />
                        {$t('Add New Template')}
                    </Button>
                </div>
            </div>
            <div className="inbud-table_wrapper">
                <table className="inbud-table">
                    <thead>
                        <tr>
                            <th>{$t('Name')}</th>
                            <th>{$t('Category')}</th>
                            <th>{$t('Description')}</th>
                            <th>{$t('Created By')}</th>
                            <th>{$t('Added By')}</th>
                            <th>{$t('Attachment')}</th>
                            <th>{$t('Action')}</th>
                        </tr>
                    </thead>
                    {!isLoading && templates.length > 0 && (
                        <tbody>
                            {templates.map((template) => {
                                return (
                                    <ExpenseLibraryTemplatesIndexTableRow
                                        key={template.id}
                                        template={template}
                                        clickRow={clickRow}
                                        deleteRow={deleteRow}
                                        templatesShowUrl={props.templatesShowUrl}
                                        templatesExportUrl={props.templatesExportUrl}
                                    />
                                );
                            })}
                        </tbody>
                    )}
                    {isLoading && (
                        <tbody>
                            <tr>
                                <td colSpan="6">{$t('Loading')}...</td>
                            </tr>
                        </tbody>
                    )}
                    {!isLoading && templates.length === 0 && (
                        <tbody>
                            <tr>
                                <td colSpan="7">
                                    <a href="#" onClick={() => setUpsertedTemplate({})}>
                                        {$t('No Templates found click here to Add New Template')}
                                    </a>
                                </td>
                            </tr>
                        </tbody>
                    )}
                </table>
            </div>

            <ExpenseLibraryTemplatesEditModal
                modalId={'edit-template'}
                modalRef={expenseLibraryTemplatesEditModalRef}
                modalElement={expenseLibraryTemplatesEditModalElement}
                closeHandler={closeModal}
                successHandler={successHandler}
                upsertedTemplate={upsertedTemplate}
                templatesCreateUrl={props.templatesCreateUrl}
                templatesStoreUrl={props.templatesStoreUrl}
                templatesUpdateUrl={props.templatesUpdateUrl}
            />
            <ConfirmationModal
                closeHandler={closeModal}
                successHandler={successHandler}
                triggerAction={triggerDeleteTemplate}
                cleanStates={cleanConfirmationModalStates}
                allowAction={deletedTemplate !== undefined}
                modalId={'delete-template'}
                modalRef={expenseLibraryTemplatesDeleteModalRef}
                modalElement={expenseLibraryTemplatesDeleteModalElement}
                request={actionInProgress}
                actionCompleted={actionCompleted}
                actionFailed={actionFailed}
                confirmationMsg={$t('Are you sure you want to delete {{name}}?', { name: deletedTemplate?.name })}
                actionCompletedMsg={$t('{{name}} deleted', { name: deletedTemplate?.name })}
                actionFailedMsg={$t('{{name}} could not be deleted', { name: deletedTemplate?.name })}
                title={$t('Delete Template')}
                cancelButtonText={$t('Cancel')}
                primaryButtonText={$t('Delete')}
            />
        </div>
    );
}
